import { h, render } from 'vue';
import {
    aiPageBuilderBlocks,
    parseMfComponentConfig,
} from './component-blocks';
import { mountInteractiveRuntime } from './interactive-runtime';
import {
    attachResolvedMediaEntriesToContent,
    extendAppContextWithMediaEntries,
} from './media-lookup';

const showFallback = (wrapper, type) => {
    wrapper.innerHTML = `<div class="p-4 border border-dashed border-gray-300 rounded text-gray-500 text-sm text-center">Component: ${type}</div>`;
};

export const hydrateMfComponents = (
    root,
    { appContext = null, containerRef = '', mediaEntryMap = {} } = {}
) => {
    const mountedWrappers = new Set();
    let cleanupInteractiveRuntime = null;

    if (!root) {
        return () => {};
    }

    const mfComponents = root.querySelectorAll('mf-component');
    let leadCaptureIndex = 0;

    for (const el of mfComponents) {
        const type = el.getAttribute('type');
        const config = attachResolvedMediaEntriesToContent(
            parseMfComponentConfig(el),
            mediaEntryMap
        );
        const uuid =
            type === 'lead-capture'
                ? 'ai-lead-capture-' + leadCaptureIndex++
                : crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);

        const wrapper = document.createElement('div');
        el.replaceWith(wrapper);

        try {
            const component = aiPageBuilderBlocks[type];

            if (!component) {
                showFallback(wrapper, type);
                continue;
            }

            const vnode = h(component, {
                content: config,
                containerRef,
                uuid,
            });

            if (appContext) {
                vnode.appContext = extendAppContextWithMediaEntries(
                    appContext,
                    mediaEntryMap
                );
            }

            render(vnode, wrapper);
            mountedWrappers.add(wrapper);
        } catch (e) {
            console.warn(`Failed to hydrate component: ${type}`, e);
            render(null, wrapper);
            showFallback(wrapper, type);
        }
    }

    cleanupInteractiveRuntime = mountInteractiveRuntime(root);

    return () => {
        cleanupInteractiveRuntime?.();
        cleanupInteractiveRuntime = null;

        for (const wrapper of mountedWrappers) {
            render(null, wrapper);
        }

        mountedWrappers.clear();
    };
};
